import { useMemo } from "react";

import { components } from "@/@types/api";
import { isDefined } from "@/Utils/array-utils";

export type ScenarioResultRevenue = components["schemas"]["ScenarioResultRevenue"];
export type ScenarioResultSpend = components["schemas"]["ScenarioResultSpend"];

interface UseScenarioGeographyDataOptions {
  revenues: ScenarioResultRevenue[];
  spends: ScenarioResultSpend[];
  selectedGeography?: string | null;
}

interface ScenarioGeographyData {
  revenues: ScenarioResultRevenue[];
  spends: ScenarioResultSpend[];
  availableGeographies: string[];
}

/**
 * Hook that filters and aggregates scenario data based on selected geography.
 * When selectedGeography is null/undefined, returns all data (aggregated by backend).
 * When selectedGeography is provided, filters data to only that geography.
 */
export const useScenarioGeographyData = ({
  revenues,
  spends,
  selectedGeography,
}: UseScenarioGeographyDataOptions): ScenarioGeographyData => {
  // Get all unique geographies from the data
  const availableGeographies = useMemo(() => {
    const revenueGeos = revenues.map((r) => r.geography).filter(isDefined);
    const spendGeos = spends.map((s) => s.geography).filter(isDefined);
    const uniqueGeos = [...new Set([...revenueGeos, ...spendGeos])]; // we get all unique geographies from the data as the source of truth
    return uniqueGeos;
  }, [revenues, spends]);

  const { filteredRevenues, filteredSpends } = useMemo(() => {
    if (!selectedGeography) {
      return {
        filteredRevenues: revenues,
        filteredSpends: spends,
      };
    }

    return {
      filteredRevenues: revenues.filter((revenue) => revenue.geography === selectedGeography),
      filteredSpends: spends.filter((spend) => spend.geography === selectedGeography),
    };
  }, [revenues, spends, selectedGeography]);

  return {
    revenues: filteredRevenues,
    spends: filteredSpends,
    availableGeographies,
  };
};
